import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import isEmpty from 'lodash/isEmpty';
import take from 'lodash/take';
import { useQuery, useMutation } from '@apollo/client';
import { useHistory } from 'react-router-dom';

import { useOrderData } from '../../../hooks/useOrderData';
import Tag from '../V2/Tag';
import Money from 'core/api/Money';
import { ReactComponent as MinusIcon } from 'core/assets/images/V2/icons/MinusIcon.svg';
import { ReactComponent as PlusIcon } from 'core/assets/images/V2/icons/PlusIcon.svg';
import { ReactComponent as EditSVG } from 'core/assets/images/V2/icons/Edit.svg';
import { ReactComponent as FreezeSVG } from 'core/assets/images/V2/icons/FreezeIcon.svg';
import { ReactComponent as SwapIcon } from 'core/assets/images/V2/icons/SwapIcon.svg';
import { PRODUCT_TEMP } from 'core/assets/images/V2';
import { Box, Button, Flex, Text, Image, Radio } from 'core/ui/atoms';
import { ADD_USER_MEAL_SWAP_REQUEST } from 'core/api/apollo/mutations';
import {
  GET_USER_SWAPS_DELIVERY_DAY,
  LIST_UNLOCKED_AVAILABLE_DELIVERY_DAYS,
} from 'core/api/apollo/queries';
import { selectMealPath, productDescriptionPath } from 'onramp/api/navigation';

const OrderItemMeal = ({ item, deliveryDayId, isAddOn = false, refetchBillingSummary }) => {
  const history = useHistory();
  const { id, allergens, swapped, editable, images, dietTags, name, subtitle, freezerFriendly, quantity } = item;
  const [updatedQuantity, setUpdatedQuantity] = useState(quantity);

  const imageURL = images?.[0]?.smallUrl || PRODUCT_TEMP;

  const { refetchOrders } = useOrderData();

  const {
    data: { listUnlockedAvailableDeliveryDays = [] } = {},
  } = useQuery(LIST_UNLOCKED_AVAILABLE_DELIVERY_DAYS, {
    skip: !isAddOn,
  });

  const deliveryDays = listUnlockedAvailableDeliveryDays.filter((day) => !day.locked && day.menuPublished);
  const deliveryDayIds = deliveryDays.map((day) => day.id);
  const swapVariables = { deliveryDayIds: deliveryDayIds.toString() };

  const {
    data: { getUserSwapsForDeliveryDay = [] } = {},
    // eslint-disable-next-line no-unused-vars
    refetch,
  } = useQuery(GET_USER_SWAPS_DELIVERY_DAY, {
    variables: swapVariables,
    skip: deliveryDayIds.length === 0 && !isAddOn,
    // fetchPolicy: 'network-only',
  });

  const addonsItems = getUserSwapsForDeliveryDay.find((day) => day.dayId === deliveryDayId)?.addons || [];
  const addonQuantity = isAddOn && addonsItems.find((addon) => addon.originalComponentName === item.name)?.quantity || 0;
  // const addonPrice = isAddOn && addonsItems.find((addon) => addon.originalComponentName === item.name)?.price || 0;

  useEffect(() => {
    if (isAddOn) {
      setUpdatedQuantity(addonQuantity);
    }
    // eslint-disable-next-line no-console
    console.log('Updated quantity set to:', addonQuantity);
  }, [addonQuantity, isAddOn]);


  // eslint-disable-next-line no-unused-vars
  const [addUserMealSwapRequest, { loading: mutationLoading }] = useMutation(ADD_USER_MEAL_SWAP_REQUEST, {
    onCompleted: async (data) => {
      // eslint-disable-next-line no-console
      console.log('Meal swap request added successfully:', data);
      refetchOrders();
      // Also refetch billing summary to update the PAY NOW amount
      if (refetchBillingSummary) {
        await refetchBillingSummary();
      }
    },
    onError: (error) => {
      // eslint-disable-next-line no-console
      console.error('Error adding meal swap request:', error);
    },
  });

  const handleQuantityChange = async (value) => {
    setUpdatedQuantity(value + updatedQuantity);

    try {
      await addUserMealSwapRequest({
        variables: {
          input: {
            deliveryDayId: deliveryDayId,
            originalComponentId: item.id,
            requestType: 'Addon',
            quantity: value,
          },
        },
      });
      refetchOrders();
      // Also refetch billing summary to update the PAY NOW amount
      if (refetchBillingSummary) {
        await refetchBillingSummary();
      }
    } catch (error) {
      console.error('Failed to update quantity:', error);
      setUpdatedQuantity(quantity);
    }
  };

  const onSelectEditMeal = () => {
    history.push(selectMealPath(id, deliveryDayId));
  };

  const RenderTags = () => {
    if (isEmpty(dietTags)) return null;
    return (
      <TagContainer direction='row' gap="10px">
        {take([dietTags], 3)?.map((tag, index) => <Tag text={tag} key={index} />)}
      </TagContainer>
    );
  };

  const onHandleDetails = () => {
    history.push(productDescriptionPath(deliveryDayId, item?.id));
  };

  const QuantityCounter = () => {
    return (
      <CounterSection direction='row' alignItems='center' justifyContent='space-around'>
        <MinusIcon onClick={() => updatedQuantity >= 1 && handleQuantityChange(-1)} />
        <Text>{updatedQuantity}</Text>
        <PlusIcon onClick={() => updatedQuantity < 1 && handleQuantityChange(1)} />
      </CounterSection>
    );
  };

  return (
    <ProductContainer>
      <ItemContainer direction='row' justifyContent='start'>
        <SelectCont>
          <ProductImage src={imageURL} alt={name} />
          {isAddOn && editable &&
            <EditBadge>
              <EditSVG />
            </EditBadge>
          }
          {!isAddOn && swapped &&
            <EditBadge>
              <SwapIcon />
            </EditBadge>
          }
          {freezerFriendly &&
            <FreezeBadge>
              <FreezeSVG />
            </FreezeBadge>
          }
        </SelectCont>
        <DetailsContainer>
          <ProductName>{name}</ProductName>
          <RenderTags />
          {!isAddOn && <ProductDescription>{subtitle}</ProductDescription>}
          {allergens && !isAddOn &&
            <ProductAllergens alignItems="center">
              Allergens : {allergens.join(', ')}
            </ProductAllergens>
          }
        </DetailsContainer>
      </ItemContainer>
      {isAddOn &&
        <DetailSection direction='row' alignItems='center' justifyContent='space-around'>
          <Radio
            name={item.id}
            value='0'
            label={Money(item?.cost).toFormat('$0,0')}
            checked={updatedQuantity > 0}
            // onChange={this.onChange}
          />
          <QuantityCounter />
          <ViewDetailButton onClick={onHandleDetails}>View details</ViewDetailButton>
        </DetailSection>
      }
      {item?.editable && <DetailsButton btn-unstyled btn-lg onClick={onSelectEditMeal}>SWAP</DetailsButton>}
    </ProductContainer>
  );
};

OrderItemMeal.propTypes = {
  item: PropTypes.object.isRequired,
  isAddOn: PropTypes.bool,
  deliveryDayId: PropTypes.string,
  refetchBillingSummary: PropTypes.func,
};

const ItemContainer = styled(Flex)`
  width: 100%;
`;

const TagContainer = styled(Flex)`
  margin-top: 5px;
`;

const ViewDetailButton = styled(Button)`
  width: 100px;
  font-size: 12px;
  padding: 0px;
  text-decoration: underline;
  text-underline-offset: 5px;
  text-transform: uppercase;
`;

const DetailSection = styled(Flex)`
  width: 100%;
  margin-top: 10px;
`;

const CounterSection = styled(Flex)`
  border: 1px solid;
  height: 30px;
  width: 90px;
`;

const ProductImage = styled(Image)`
  height: 94px;
  width: 94px;
  object-fit: cover;
`;

const SelectCont = styled.div`
  position: relative;
  width: 28%;
  display: flex;
  justify-content: end;
  flex-shrink: 0;
  margin-right: 10px;
  &:last-child {
    margin-right: 0;
  }
`;

const EditBadge = styled(Box)`
  height: 12px;
  width: 12px;
  border-radius: 4px;
  font-size: 12px;
  text-align: center;
  position: absolute;
  top: 0rem;
  right: 1rem;
`;

const FreezeBadge = styled(Box)`
  height: 20px;
  width: 45px;
  border-radius: 4px;
  font-size: 12px;
  text-align: center;
  position: absolute;
  bottom: 0rem;
  left: 0rem;
`;

const ProductContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  margin-bottom: 0px;
`;

const DetailsContainer = styled(Box)`
  width: 60%;
`;

const DetailsButton = styled(Button)`
  width: 160px;
  padding: 10px 32px;
  border-radius: 4px;
  text-decoration: underline;
  text-underline-offset: 5px;
`;

const ProductName = styled.p`
  font-family: ${({ theme }) => theme.typography.familyLarken};
  font-size: 16px;
  line-height: 22px;
  margin: 0px;
  color: ${(p) => p.theme.colors.primaryV2};
`;

const ProductDescription = styled.p`
  font-family: ${({ theme }) => theme.typography.familyPoppins};
  font-weight: 400;
  font-size: 12px;
  color: ${(p) => p.theme.colors.primaryV2};
  margin: 0px;
  margin-top: 2px;
`;

const ProductAllergens = styled(Flex)`
  font-family: ${({ theme }) => theme.typography.familyPoppins};
  font-weight: 500;
  font-style: italic;
  font-size: 12px;
  padding-bottom: 12px;
  margin-top: 4px;
`;

export default OrderItemMeal;
